/**
 * Utility functions for parsing URN links in GekkoVet Copilot responses
 */

export interface ParsedURN {
  /** The original URN string */
  urn: string;
  /** The entity type (e.g., 'disease', 'drug', 'symptom', 'case') */
  entityType: string;
  /** The entity ID (number for most entities, string for cases with UUIDs) */
  entityId: number | string;
  /** Whether this is a valid GekkoVet URN */
  isValid: boolean;
}

export interface ParsedSourcesShortcode {
  /** The original shortcode string */
  shortcode: string;
  /** Array of source IDs */
  sourceIds: number[];
  /** Whether this is a valid Sources shortcode */
  isValid: boolean;
}

/**
 * Parse a URN string to extract entity information
 * Expected format: urn:gekkovet:entity_type:id
 * Example: urn:gekkovet:disease:123
 */
export function parseURN(urnString: string): ParsedURN {
  const result: ParsedURN = {
    urn: urnString,
    entityType: '',
    entityId: 0,
    isValid: false,
  };

  try {
    // URN format: urn:gekkovet:entity_type:id
    const parts = urnString.split(':');
    
    // Must have exactly 4 parts
    if (parts.length !== 4) {
      return result;
    }

    const [scheme, namespace, entityType, idStr] = parts;

    // Validate scheme and namespace
    if (scheme !== 'urn' || namespace !== 'gekkovet') {
      return result;
    }

    // Validate entity type
    const validEntityTypes = ['disease', 'drug', 'symptom', 'procedure', 'source', 'case', 'document'];
    if (!validEntityTypes.includes(entityType)) {
      return result;
    }

    // Validate and parse ID
    // For cases and documents, IDs are UUIDs (strings), for other entities they are numeric
    if (entityType === 'case' || entityType === 'document') {
      // For cases, validate that the ID is a UUIDv4 format
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      if (!idStr || !uuidRegex.test(idStr)) {
        return result;
      }
      // Store as string for cases (UUIDv4)
      result.entityType = entityType;
      result.entityId = idStr; // Store UUID as string
      result.isValid = true;
    } else {
      // For other entities, parse as integer
      const entityId = parseInt(idStr, 10);
      if (isNaN(entityId) || entityId <= 0) {
        return result;
      }
      // All validation passed
      result.entityType = entityType;
      result.entityId = entityId;
      result.isValid = true;
    }

    return result;
  } catch (error) {
    console.warn('Error parsing URN:', urnString, error);
    return result;
  }
}

/**
 * Check if a string is a valid GekkoVet URN
 */
export function isValidGekkoVetURN(urnString: string): boolean {
  return parseURN(urnString).isValid;
}

/**
 * Create a GekkoVet URN from entity type and ID
 */
export function createURN(entityType: string, entityId: number): string {
  return `urn:gekkovet:${entityType}:${entityId}`;
}

/**
 * Parse a Sources shortcode to extract source IDs
 * Expected format: [Sources:id1,id2,id3] or [Sources: id1,id2,id3] (with optional space)
 * Example: [Sources:1,2,3]
 */
export function parseSourcesShortcode(shortcode: string): ParsedSourcesShortcode {
  const result: ParsedSourcesShortcode = {
    shortcode,
    sourceIds: [],
    isValid: false,
  };

  try {
    // Sources shortcode format: [Sources:id1,id2,id3] or [Sources: id1, id2, id3] (tolerant of spaces)
    const match = shortcode.match(/^\[Sources:\s*([0-9,\s]+)\]$/);
    
    if (!match) {
      return result;
    }

    const idsString = match[1];
    // Split by comma and remove any whitespace from each ID
    const ids = idsString.split(',').map(id => parseInt(id.trim(), 10));
    
    // Validate all IDs are valid numbers
    if (ids.some(id => isNaN(id) || id <= 0)) {
      return result;
    }

    // All validation passed
    result.sourceIds = ids;
    result.isValid = true;

    return result;
  } catch (error) {
    console.warn('Error parsing Sources shortcode:', shortcode, error);
    return result;
  }
}

/**
 * Check if a string is a valid Sources shortcode
 */
export function isValidSourcesShortcode(shortcode: string): boolean {
  return parseSourcesShortcode(shortcode).isValid;
}

/**
 * Create a Sources shortcode from source IDs
 */
export function createSourcesShortcode(sourceIds: number[]): string {
  return `[Sources:${sourceIds.join(',')}]`;
}
